import { ROOM_TTL_MS } from '@xox/shared'
import { WifiOff } from 'lucide-react'
import type { ConnectionStatus } from '../lib/socket'
import type { RoomError } from '../store/shared'
import { Toast } from './Toast'
import { Button, Card, Centered } from './ui'

const ROOM_TTL_MINUTES = Math.round(ROOM_TTL_MS / 60_000)

/** What every live room renders first: the error toast and the reconnecting pill. */
export function RoomChrome({
  error,
  onDismiss,
  status,
}: {
  error: RoomError | null
  onDismiss: () => void
  status: ConnectionStatus
}) {
  return (
    <>
      <Toast error={error} onDismiss={onDismiss} />
      <ConnectionBanner status={status} />
    </>
  )
}

/** Floating pill shown while the room socket is (re)connecting. */
function ConnectionBanner({ status }: { status: ConnectionStatus }) {
  if (status === 'open') return null
  return (
    <div
      role="status"
      className="fixed inset-x-0 top-[calc(env(safe-area-inset-top)+3.25rem)] z-20 mx-auto w-fit rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-white shadow"
    >
      <WifiOff className="mr-1 inline size-3.5" aria-hidden />
      {status === 'connecting' ? 'Connecting…' : 'Reconnecting…'}
    </div>
  )
}

interface ClosedProps {
  /** Why the room is gone; `isRoomGone` never lets 'restart' reach this component. */
  closedReason: 'expired' | 'closed'
  noun: 'room' | 'race'
  onBack: () => void
  backLabel: string
}

/** Full-column card for a room that no longer exists. */
export function RoomClosedCard({ closedReason, noun, onBack, backLabel }: ClosedProps) {
  const hostLeft = closedReason === 'closed'
  return (
    <Centered>
      <Card className="text-center">
        <h1 className="text-xl font-bold">
          {hostLeft ? `The host ended this ${noun}` : `This ${noun} timed out`}
        </h1>
        <p className="mt-1 text-slate-500">
          {hostLeft
            ? `When the person who created a ${noun} leaves, it closes for everyone.`
            : `Rooms expire after ${ROOM_TTL_MINUTES} minutes of inactivity.`}
        </p>
        <Button className="mt-4" onClick={onBack}>
          {backLabel}
        </Button>
      </Card>
    </Centered>
  )
}

/** True when the page should show RoomClosedCard instead of the game. */
export function isRoomGone(room: {
  status: ConnectionStatus
  closedReason: 'expired' | 'restart' | 'closed' | null
  snapshot: unknown
}): boolean {
  if (room.closedReason === 'expired' || room.closedReason === 'closed') return true
  return room.status === 'closed' && !room.snapshot
}

export function JoiningNotice({ label }: { label: string }) {
  return (
    <Centered>
      <div className="animate-pulse text-slate-500">{label}</div>
    </Centered>
  )
}
